import {
  GET_ENDPOINTS_QUERY_KEY,
  ENDPOINTS_METADATA_QUERY_KEY,
} from 'features/Endpoints/useEndpointsQuery'
import {AI_USAGE_SUMMARY_QUERY_KEY} from 'features/Analytics/AIUsage/hooks/useAIUsageSummaryQuery'
import {AI_USAGE_APPS_QUERY_KEY} from 'features/Analytics/AIUsage/hooks/useAIUsageAppsQuery'
import {AI_USAGE_USERS_QUERY_KEY} from 'features/Analytics/AIUsage/hooks/useAIUsageUsersQuery'
import {SUBSCRIPTION_DETAILS_QUERY_KEY} from 'features/Settings/Billing/useSubscriptionDetailsQuery'
import {BLOCK_PAGES_QUERY_KEY} from 'features/Settings/BlockPages/useBlockPagesQuery'
import {
  SIEM_HTTP_QUERY_KEY,
  SIEM_S3_QUERY_KEY,
} from 'features/Settings/SIEM/hooks'
import {TENANT_CONFIG_QUERY_KEY} from 'hooks/useTenantConfigQuery'
import {
  PrefetchContextEnum,
  PrefetchQueryFnEnum,
  EndpointRoutesEnum,
  PolicyRoutesEnum,
  SettingsRoutesEnum,
  CASBRoutesEnum,
  AnalyticsRoutesEnum,
} from 'interfaces'
import {PERMISSIONS} from 'rbac'

// Prefetch global policy keys list.
export const GLOBAL_PREFETCH_KEYS = [
  TENANT_CONFIG_QUERY_KEY,
  'getPolicies',
  GET_ENDPOINTS_QUERY_KEY,
  'getAdminsList',
  'getCASBOverview',
  'getCASBApps',
]

/**
 * Prefetch configuration
 */
export const prefetchConfig = {
  global: [
    {
      page: PolicyRoutesEnum.BASE,
      isReactQuery: true,
      queries: [
        {
          queryKey: ['getPolicies'],
          queryFn: PrefetchQueryFnEnum.FETCH_POLICIES_DATA,
          reqPermissions: [PERMISSIONS.VIEW_POLICIES.READ],
        },
        {
          queryKey: [TENANT_CONFIG_QUERY_KEY],
          queryFn: PrefetchQueryFnEnum.FETCH_TENANT_CONFIG_DATA,
          reqPermissions: [PERMISSIONS.VIEW_POLICIES.READ],
        },
      ],
    },
    {
      page: EndpointRoutesEnum.BASE,
      isReactQuery: true,
      queries: [
        {
          queryKey: [GET_ENDPOINTS_QUERY_KEY],
          queryFn: PrefetchQueryFnEnum.FETCH_ENDPOINTS_DATA,
          reqPermissions: [PERMISSIONS.VIEW_ENDPOINTS.READ],
        },
        {
          queryKey: ENDPOINTS_METADATA_QUERY_KEY,
          queryFn: PrefetchQueryFnEnum.FETCH_ENDPOINTS_METADATA,
          reqPermissions: [PERMISSIONS.VIEW_ENDPOINTS.READ],
        },
      ],
    },
    {
      page: SettingsRoutesEnum.GENERAL,
      isReactQuery: true,
      queries: [
        {
          queryKey: ['getAdminsList'],
          queryFn: PrefetchQueryFnEnum.FETCH_SETTINGS_GENERAL_DATA,
          reqPermissions: [PERMISSIONS.VIEW_ADMINS.READ],
        },
      ],
    },
    {
      page: CASBRoutesEnum.BASE,
      isReactQuery: true,
      queries: [
        {
          queryKey: ['getCASBOverview'],
          queryFn: PrefetchQueryFnEnum.FETCH_CASB_OVERVIEW_DATA,
          reqPermissions: [PERMISSIONS.VIEW_CASB_OVERVIEW.READ],
        },
        {
          queryKey: ['getCASBFiles'],
          queryFn: PrefetchQueryFnEnum.FETCH_CASB_FILES_DATA,
          reqPermissions: [PERMISSIONS.VIEW_CASB_TABLE.READ],
        },
        {
          queryKey: ['getCASBApps'],
          queryFn: PrefetchQueryFnEnum.FETCH_CASB_APPLICATIONS_DATA,
          reqPermissions: [PERMISSIONS.VIEW_CASB_OVERVIEW.READ],
        },
      ],
    },
    {
      page: AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS,
      isReactQuery: true,
      queries: [
        {
          queryKey: [AI_USAGE_SUMMARY_QUERY_KEY],
          queryFn: PrefetchQueryFnEnum.FETCH_AI_USAGE_SUMMARY_DATA,
          reqPermissions: [PERMISSIONS.ANALYTICS.READ],
        },
        {
          queryKey: [AI_USAGE_APPS_QUERY_KEY],
          queryFn: PrefetchQueryFnEnum.FETCH_AI_USAGE_APPS_DATA,
          reqPermissions: [PERMISSIONS.ANALYTICS.READ],
        },
      ],
    },
    // ...other pages
  ],
  contextual: {
    [EndpointRoutesEnum.BASE]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          id: PrefetchQueryFnEnum.WARM_ENDPOINT_DIAGNOSTICS,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_ENDPOINTS.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_ASSOCIATED_USERS,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_ENDPOINTS.READ],
        },
      ],
    },
    [AnalyticsRoutesEnum.ANALYTICS_OVERVIEW]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          queryFnName:
            PrefetchQueryFnEnum.FETCH_ANALYTICS_POLICIES_7DAY_TREND_DATA,
          isReactQuery: true,
          queryKey: ['get7DayTrend'],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_POLICIES.READ],
        },
        {
          queryFnName:
            PrefetchQueryFnEnum.FETCH_ANALYTICS_POLICIES_TOP_BLOCKS_DATA,
          isReactQuery: true,
          queryKey: ['getTopTenBlocks', {}],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_POLICIES.READ],
        },
        {
          queryFnName: PrefetchQueryFnEnum.FETCH_ANALYTICS_PRODUCTIVITY_DATA,
          isReactQuery: true,
          queryKey: ['getProductivity', {}],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_PRODUCTIVITY.READ],
        },
        {
          queryFnName:
            PrefetchQueryFnEnum.FETCH_ANALYTICS_PRODUCTIVITY_ACTIVITY_TIME_DATA,
          isReactQuery: true,
          queryKey: [
            'getActivityByTime',
            {
              activityByTimeFormat: 'Week',
              params: {},
            },
          ],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_PRODUCTIVITY.READ],
        },
        {
          queryFnName:
            PrefetchQueryFnEnum.FETCH_ANALYTICS_SHADOW_IT_TOP_APPS_DATA,
          isReactQuery: true,
          queryKey: ['getShadowITTopApps', '', ''],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_SHADOW_IT.READ],
        },
        {
          queryFnName:
            PrefetchQueryFnEnum.FETCH_ANALYTICS_SHADOW_IT_STREAM_CHARTS_DATA,
          isReactQuery: true,
          queryKey: ['getShadowITStreamChartData', 'application', '', ''],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS_SHADOW_IT.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_ANALYTICS_SEARCH,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ENDPOINTS_SEARCH.READ],
        },
      ],
    },
    [PolicyRoutesEnum.BASE]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_CATEGORIES,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_POLICY_CONTENT.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_BLOCK_PAGES,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.USE_BLOCK_PAGES.READ],
        },
      ],
    },
    [PolicyRoutesEnum.POLICIES_CONTENT]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_ASSIGNMENT,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_POLICY_USERS.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_BLOCK_PAGES,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.BLOCK_PAGES.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_CAC_APP,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_POLICY_CAC.READ],
        },
        {
          id: PrefetchQueryFnEnum.WARM_POLICY_BYPASS,
          isReactQuery: false,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.POLICY_BYPASS.READ],
        },
      ],
    },
    [SettingsRoutesEnum.GENERAL]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          queryKey: ['api-keys-list'],
          queryFnName: PrefetchQueryFnEnum.FETCH_API_CREDS_DATA,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.API_KEYS.READ],
        },
        {
          queryKey: [BLOCK_PAGES_QUERY_KEY],
          queryFnName: PrefetchQueryFnEnum.FETCH_BLOCK_PAGES_DATA,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.BLOCK_PAGES.READ],
        },
        {
          queryKey: [SIEM_HTTP_QUERY_KEY],
          queryFnName: PrefetchQueryFnEnum.FETCH_SIEM_HTTP_DATA,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.SIEM_HTTP.READ],
        },
        {
          queryKey: [SIEM_S3_QUERY_KEY],
          queryFnName: PrefetchQueryFnEnum.FETCH_SIEM_S3_DATA,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ADD_S3_BUCKET_LOCATION.READ],
        },
        {
          queryKey: [SUBSCRIPTION_DETAILS_QUERY_KEY],
          queryFnName: PrefetchQueryFnEnum.FETCH_BILLING_DATA,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.BILLING_DETAILS.READ],
        },
      ],
    },
    [CASBRoutesEnum.BASE]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          queryKey: ['CASBReviewFileInfo'],
          queryFnName: PrefetchQueryFnEnum.FETCH_CASB_FILE_DETAILS,
          isReactQuery: true,
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.VIEW_CASB_TABLE.READ],
        },
      ],
    },
    [AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS]: {
      [PrefetchContextEnum.MOUNT]: [
        {
          queryFnName: PrefetchQueryFnEnum.FETCH_AI_USAGE_USERS_DATA,
          isReactQuery: true,
          queryKey: [AI_USAGE_USERS_QUERY_KEY],
          defaultPayload: null,
          reqPermissions: [PERMISSIONS.ANALYTICS.READ],
        },
      ],
    },
  },
  // ...other configs
}

export const prefetchCacheTime = 24 * 60 * 60 * 1000 // 24 hours
